import React from 'react';
import PropTypes from 'prop-types';
import Hyperlink from '../Hyperlink';
import './Breadcrumbs.css';

/**
 * Breadcrumbs — the trail above a page title: DS Hyperlinks (A.Small)
 * separated by "/".
 *
 * Figma: OVER-LL `2699:17501` (the "Nav" inside Secondary Banner V2 `2699:15510`).
 *
 * - Without items nothing renders.
 * - The last crumb is the current page (`aria-current="page"`).
 * - A crumb with `href` and/or `onClick` is a link; a crumb with neither is
 *   plain text in the same type style.
 * - A click-only crumb (no `href`) never follows Hyperlink's default "#".
 * - Colours are hooks an ancestor can set (none is declared on the nav itself):
 *   `--breadcrumbs-link-color`, `--breadcrumbs-link-hover`,
 *   `--breadcrumbs-link-focus`, `--breadcrumbs-separator-color`.
 */
const Breadcrumbs = ({ items = [], ariaLabel = 'Breadcrumb', className = '', ...rest }) => {
  if (!items?.length) return null;

  const lastIndex = items.length - 1;

  return (
    <nav
      aria-label={ariaLabel}
      className={`ds-breadcrumbs${className ? ` ${className}` : ''}`}
      {...rest}
    >
      <ol className="ds-breadcrumbs__list">
        {items.map(({ label, href, onClick }, index) => {
          const current = index === lastIndex ? 'page' : undefined;
          // Hyperlink defaults to href="#" — a click-only crumb must not touch the URL.
          const handleClick = onClick
            ? (e) => {
                if (!href) e.preventDefault();
                onClick(e);
              }
            : undefined;

          return (
            <li key={`${index}-${label}`} className="ds-breadcrumbs__item">
              {index > 0 && (
                <span className="ds-breadcrumbs__separator typo-body" aria-hidden="true">/</span>
              )}
              {href || onClick ? (
                <Hyperlink
                  variant="small"
                  href={href}
                  onClick={handleClick}
                  aria-current={current}
                  className="ds-breadcrumbs__link"
                >
                  {label}
                </Hyperlink>
              ) : (
                <span className="ds-breadcrumbs__current typo-link-sm" aria-current={current}>
                  {label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

Breadcrumbs.propTypes = {
  /** The trail, first crumb first: `[{ label, href?, onClick? }]`; the last one is the current page */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      href: PropTypes.string,
      onClick: PropTypes.func,
    }),
  ),
  /** Accessible name of the nav landmark — localise it per site */
  ariaLabel: PropTypes.string,
  /** Additional CSS class on the nav */
  className: PropTypes.string,
};

export default Breadcrumbs;
